import { useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { buildWebInviteMessage } from '@/lib/inviteLinks'
import { withAuthRecovery } from '@/lib/authRecovery'
import type { TeamMemberStudioAccess } from '@/types'

type AuthUserLike = {
  id: string
  telegram_id?: number | null
}

export type TeamRole = 'member' | 'manager' | 'teacher'
export type SeniorTeacherAccessState = 'none' | 'invited' | 'active' | 'team_member' | 'team_manager'
export type SeniorTeacherAccessRow = {
  teacher_id: string
  full_name: string
  contact_email: string | null
  phone_number: string | null
  telegram_username: string | null
  user_id: string | null
  login_email: string | null
  access_state: SeniorTeacherAccessState
  team_role: string | null
  invite_id: string | null
  invite_token: string | null
  invite_email: string | null
  invite_expires_at: string | null
}
type AccessScope = 'specific' | 'all'

type CreateInviteOptions = {
  role: TeamRole
  studioIds?: number[]
  accessScope?: AccessScope
  targetEmail?: string | null
  targetFirstName?: string | null
  targetLastName?: string | null
}

export function useTeamManagement() {
  const loadMembers = useCallback(async (_user: AuthUserLike) => {
    const { data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('get_my_team_members')
    ) as { data: any[] | null; error: any }

    if (error) throw error
    return data || []
  }, [])

  const loadInvites = useCallback(async (user: AuthUserLike) => {
    const currentTime = new Date().toISOString()
    const { data, error } = await supabase
      .from('invites')
      .select('*')
      .eq('created_by', user.id)
      .eq('used', false)           // 'used' = exhausted (current_uses >= max_uses)
      .neq('role', 'senior_teacher')
      .gt('expires_at', currentTime)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }, [])

  const createInvite = useCallback(async (
    user: AuthUserLike,
    options: CreateInviteOptions
  ): Promise<any> => {
    const {
      role,
      studioIds = [],
      accessScope = 'specific',
      targetEmail = null,
      targetFirstName = null,
      targetLastName = null
    } = options

    const normalizedTargetEmail = targetEmail?.trim().toLowerCase() || null
    const normalizedTargetFirstName = targetFirstName?.trim() || null
    const normalizedTargetLastName = targetLastName?.trim() || null

    if (accessScope === 'specific' && !studioIds.length) {
      throw new Error('Выберите хотя бы одну студию для приглашения')
    }
    if (!normalizedTargetEmail || !normalizedTargetFirstName || !normalizedTargetLastName) {
      throw new Error('Укажите email, имя и фамилию сотрудника')
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedTargetEmail)) {
      throw new Error('Проверьте формат email сотрудника')
    }

    let token: string
    try {
      token = crypto.randomUUID()
    } catch {
      token = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
      })
    }

    const inviteData = {
      token,
      created_by: user.id,
      role,
      studio_ids: accessScope === 'specific' ? studioIds : null,
      access_scope: accessScope,
      person_id: null,
      max_uses: 1,
      target_email: normalizedTargetEmail,
      target_first_name: normalizedTargetFirstName,
      target_last_name: normalizedTargetLastName,
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      used: false
    }

    const { data, error } = await (supabase as any)
      .from('invites')
      .insert(inviteData)
      .select('*')
      .single()

    if (error || !data) {
      throw error ?? new Error('No data returned from insert operation')
    }

    if (!data.id) {
      throw new Error('No data returned from database or missing invite ID')
    }

    return data
  }, [])

  const revokeInvite = useCallback(async (user: AuthUserLike, inviteId: string) => {
    if (!inviteId || inviteId === 'undefined') {
      throw new Error('Invalid invite ID provided')
    }

    const { error } = await (supabase as any)
      .from('invites')
      .delete()
      .eq('id', inviteId)
      .eq('created_by', user.id)

    if (error) throw error
  }, [])

  const removeMember = useCallback(async (
    _user: AuthUserLike,
    memberUserId: string
  ) => {
    const { error } = await withAuthRecovery(() => (
      (supabase as any).rpc('remove_team_member_v2', {
        p_member_user_id: memberUserId
      })
    )) as { data: null; error: any }

    if (error) throw error
  }, [])

  const loadMemberStudios = useCallback(async (
    memberUserId: string
  ): Promise<TeamMemberStudioAccess[]> => {
    const { data, error } = await withAuthRecovery(() => (
      (supabase as any).rpc('get_team_member_studios_v2', {
        p_member_user_id: memberUserId
      })
    )) as { data: TeamMemberStudioAccess[] | null; error: any }

    if (error) throw error
    return data || []
  }, [])

  const updateMemberStudios = useCallback(async (
    memberUserId: string,
    studioIds: number[],
    role: TeamRole,
    accessScope: AccessScope = 'specific'
  ) => {
    if (accessScope === 'specific' && !studioIds.length) {
      throw new Error('У сотрудника должна остаться хотя бы одна студия')
    }

    const { error } = await withAuthRecovery(() => (
      (supabase as any).rpc('update_team_member_studios_v2', {
        p_member_user_id: memberUserId,
        p_studio_ids: studioIds,
        p_role: role,
        p_access_scope: accessScope
      })
    )) as { data: null; error: any }

    if (error) throw error
  }, [])

  const copyInviteLink = useCallback(async (token: string) => {
    const inviteMessage = buildWebInviteMessage(token)
    await navigator.clipboard.writeText(inviteMessage)
  }, [])

  const confirmMemberRemoval = useCallback(async (memberName: string) => {
    const message = `Вы забираете доступ у ${memberName} ко всем своим студиям.\n\nВы всегда можете отправить новое приглашение, если понадобится вернуть доступ.`

    return await new Promise<boolean>((resolve) => {
      const webApp = window.Telegram?.WebApp
      const supportsPopup = webApp && webApp.version && parseFloat(webApp.version) >= 6.1

      if (webApp?.showConfirm && supportsPopup) {
        try {
          webApp.showConfirm(message, (confirmed: boolean) => {
            resolve(Boolean(confirmed))
          })
          return
        } catch {
          // Fall through to browser confirm
        }
      }

      resolve(confirm(message))
    })
  }, [])

  return {
    loadMembers,
    loadInvites,
    createInvite,
    revokeInvite,
    removeMember,
    loadMemberStudios,
    updateMemberStudios,
    copyInviteLink,
    confirmMemberRemoval
  }
}
